import React, { useEffect, useState } from 'react';
import { graphql } from 'gatsby';
import styled from 'styled-components';
import { Entry, UniversePage as UniversePageTypes } from '../generated/contentful-generated-types';
import { Breadcrumb } from '../utils/breadcrumb';
import { BreadcrumbsV2 } from '../components/breadcrumb/BreadcrumbsV2';
import { TBreadcrumb } from '../components/breadcrumb';
import { useRichText } from '../hooks/richtext/useRichText';
import { DebugContentfulEntry } from '../components/Debug/DebugContentfulEntry';
import { getColorFromCode } from '../common/constants';
import { useTranslation } from 'react-i18next';
import { ImageCompressionCDN, ImageFormatAndCompressionCDN, ImageFormatCDN, getImageCDNUri } from '../utils/CDN-fetcher';
import { getSeoInformations } from '../utils/contentful';
import { SeoHelmet } from '../components/SeoHelmet';
import { CategoryGrid } from '../components/UniversePage/CategoryGrid';
import { SectionsDispatcher } from '../components/UniversePage/SectionsDispatcher';
import { CurrentSelection } from '../components/UniversePage/CurrentSelection';
const back = require('../../assets/icons/back.svg');
import { VARIABLE_URLS, getUrlOf } from '../utils/urls';
import { WithAlternates } from '../common/custom-gql-types';
import { getAlternateLinksFromBreadcrumbs } from '../components/ProductPageV3/ProductPageHelmet';
import { ReinsuranceMobile } from '../components/Reinsurance/ReinsuranceMobile';
import { ReinsuranceDesktop } from '../components/Reinsurance/ReinsuranceDesktop';

interface Props {
  data: {
    contentful: { universePage: WithAlternates<UniversePageTypes> };
    breadcrumb?: Breadcrumb;
  };
  pageContext: {
    id: string;
    breadcrumbs: TBreadcrumb[];
    canonical?: string;
    pagePath: string;
    pageType: Entry;
    locale: string;
  };
}

const UniversePage = (props: Props) => {
  const {
    data: {
      contentful: {
        universePage: {
          sys: { id: universePageId },
          headerContent,
          currentSelectionTitle,
          currentSelectionCollection,
          hideCurrentSelection
        },
        universePage
      }
    },
    pageContext: { breadcrumbs }
  } = props;
  const customCompression: ImageCompressionCDN = 2;
  const formatAndCompression: ImageFormatAndCompressionCDN = { format: ImageFormatCDN.png, compression: customCompression };
  const [expanded, setExpanded] = useState(false);
  const title = universePage.headerContent?.title;
  const menuLabel = title as string;
  const { t } = useTranslation('translations');

  const toggleDescription = () => {
    setExpanded(!expanded);
  };
  const sections = universePage?.sectionsCollection;

  const { metaTitle, metaDescription, metaRobotsStr } = getSeoInformations(
    menuLabel,
    universePage.seoMetaTags,
    t('gatsby.category.seo.inknpaper.metaDescription', { name: menuLabel }),
    t('gatsby.category.seo.inknpaper.metaTitle', { name: menuLabel })
  );

  const canonicalSlug = universePage.seoMetaTags?.canonicalSlug || undefined;
  const canonical = canonicalSlug ? `${getUrlOf(VARIABLE_URLS.phoenix)}${canonicalSlug}` : undefined;

  const breadcrumbAlternateLinks = props.pageContext.breadcrumbs ? getAlternateLinksFromBreadcrumbs(props.pageContext.breadcrumbs) : ([] as JSX.Element[]);
  const alternateLinks = breadcrumbAlternateLinks.length ? (
    breadcrumbAlternateLinks
  ) : (
    <link rel="alternate" hrefLang={process.env.GATSBY_LOCALE} href={props.pageContext.canonical} />
  );

  // const formatAndCompression: ImageFormatAndCompressionCDN = { format: ImageFormatCDN.png, compression: customCompression };

  const [isPastPageHeight, setIsPastPageHeight] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;

      // Check if the scroll position is beyond one page height
      if (window.scrollY > windowHeight) {
        setIsPastPageHeight(true);
      } else {
        setIsPastPageHeight(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <StyledUniversePageContainer data-testid="universe-page">
      <StyledScrollTopButton className={'scroll-top-button ' + (isPastPageHeight ? 'is-visible' : 'is-not-visible')} onClick={scrollToTop}>
        <StyledScrollTopImage src={back} />
        <span className="label">{t('universe.scrollTop')}</span>
      </StyledScrollTopButton>

      <SeoHelmet
        metaTitle={metaTitle}
        metaDescription={metaDescription}
        metaRobotsStr={metaRobotsStr}
        alternateLinks={alternateLinks}
        canonical={canonical}
      ></SeoHelmet>
      <StyledMainContent>
        <StyledHeader theme={headerContent?.charterTheme}>
          <StyledDescription>
            <DebugContentfulEntry entryId={universePageId} />
            <BreadcrumbsV2 breadcrumbs={breadcrumbs as TBreadcrumb[]} />
            <StyledUniverseTitle data-testid="universe-title">{headerContent?.title}</StyledUniverseTitle>
            <StyledUniverseDesc data-testid="universe-description" className={expanded ? 'expand' : ''}>
              {useRichText(headerContent?.description)}
            </StyledUniverseDesc>
            <StyledViewLink onClick={toggleDescription}>{expanded ? t('universe.seeLess') : t('universe.seeMore')}</StyledViewLink>
          </StyledDescription>
          {headerContent?.media?.url && (
            <StyledImage data-testid="universe-image">
              <img
                src={getImageCDNUri({
                  url: headerContent.media.url,
                  width: 357,
                  height: 250,
                  formatAndCompression
                })}
              />
            </StyledImage>
          )}
        </StyledHeader>
        {/* we really should create a component "ContentBoxTile" with a Contentbox in Input. We also really should create a component "ContentBoxList" that takes a list of Content Box as an input; because we have this behaviour on several pages : Universe Page, Category Page, Home Page */}
        <>
          {universePage?.categoryPagesCollection && <CategoryGrid contentBoxCollection={universePage.categoryPagesCollection} />}
          <CurrentSelection
            currentSelectionCollection={currentSelectionCollection}
            currentSelectionTitle={currentSelectionTitle}
            hideCurrentSelection={hideCurrentSelection}
          />
          {sections && <SectionsDispatcher sections={sections} />}

          <ReinsuranceMobile />
          <ReinsuranceDesktop />
        </>
      </StyledMainContent>
    </StyledUniversePageContainer>
  );
};

export default UniversePage;

export const query = graphql`
  query UniversePage($id: String!, $preview: Boolean, $locale: String!) {
    contentful {
      universePage(id: $id, preview: $preview, locale: $locale) {
        sys {
          id
        }
        slug
        alternateFR: slug(locale: "fr")
        alternateES: slug(locale: "es")
        headerContent {
          title
          description {
            json
          }
          media {
            url
          }
          charterTheme
        }
        categoryPagesCollection {
          total
          items {
            linkedFrom {
              campaignCollection(locale: "fr") {
                items {
                  name
                  thumbnailPatch
                  discountPercentage
                  campaignDiscountCode(locale: $locale)
                  campaignDeadline
                  campaignCountdown
                  campaignConditions
                  country
                  showOnlyFor
                  showOnlyForMultiple
                }
              }
            }
            thumbnailImage {
              url
              title
            }
            thumbnailTitle
            content {
              ... on Contentful_Menu {
                title
                slug
              }
            }
          }
        }
        sectionsCollection {
          items {
            sectionTitle
            sectionType
            itemsCollection {
              items {
                ...contentBox
                ... on Contentful_SimpleContent {
                  __typename
                  title
                  subtitle
                  buttonLabel
                  simpleContentDescription: description {
                    json
                  }
                  charterTheme
                  relatedContent {
                    ...contentBox
                  }
                  media {
                    url
                  }
                }
                ...conceptBlock
              }
            }
          }
        }
        currentSelectionTitle
        hideCurrentSelection
        currentSelectionCollection {
          items {
            sys {
              publishedVersion
            }
            showOnlyFor
            showOnlyForMultiple
            selectionItemsCollection(limit: 24) {
              items {
                ...contentBox
              }
            }
          }
        }

        seoMetaTags {
          ...seoMetaTags
        }
      }
    }
  }
`;

const StyledMainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  @media (max-width: 380px) {
    gap: 24px;
  }
  .reinsurance-mobile {
    @media (max-width: 1024px) {
      margin-top: 0px;
      display: block;
    }
  }
  .faq-section {
    margin: 0px 0px;
  }
`;

const StyledHeader = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  justify-content: space-between;

  min-height: 250px;
  height: auto;
  background-color: ${props => getColorFromCode(props.theme, '#ffffff')};

  @media (max-width: 612px) {
    flex-direction: column;
    align-items: center;

    margin-left: 4px;
    margin-right: 4px;
    margin-bottom: 32px;
  }
  @media (max-width: 380px) {
    margin-bottom: 0px;
  }
`;

const StyledDescription = styled.div`
  display: flex;
  width: calc(2 / 3 * 100%);
  height: fit-content;
  flex-direction: column;
  padding: 24px;
  position: relative;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const StyledImage = styled.div`
  background-image: url(${props => props.url});
  background-size: cover;
  display: inline-flex;
  width: calc(1 / 3 * 100%);
  height: auto;
  justify-content: end;
  > img {
    object-fit: cover;
  }

  @media (max-width: 576px) {
    display: block;
  }
`;

const StyledUniverseTitle = styled.h1`
  font-family: 'Styrene Medium';
  font-style: normal;
  font-weight: 500;
  margin-bottom: 16px;
  font-size: 32px;
  line-height: 38px;
  color: #323232;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const StyledUniverseDesc = styled.div`
  color: #323232;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  margin-bottom: 6px;

  &.expand {
    display: block;
  }

  @media (max-width: 576px) {
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    display: -webkit-box;
  }
`;

const StyledViewLink = styled.div`
  cursor: pointer;
  color: var(--black-500, #323232);
  font-family: 'Styrene Medium';
  font-size: 14px;
  font-style: normal;

  line-height: 18px;
  text-decoration-line: underline;
  display: none;

  @media (max-width: 576px) {
    display: block;
  }
`;

const StyledUniversePageContainer = styled.div`
  position: relative;
  .card-title {
    @media (max-width: 768px) {
      font-size: 14px;
    }
  }
`;
const StyledScrollTopButton = styled.button`
  position: fixed;
  bottom: 70px;
  right: 20px;
  background-color: ${getColorFromCode('exa black', '#323232')};
  color: white;
  height: 50px;
  border: none;
  cursor: pointer;
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  z-index: 100;
  transition: opacity 1s;
  &.is-not-visible {
    height: 0px;
    width: 0px;
    right: -20px;
    opacity: 0.5;
    & * {
      display: none;
    }
  }
  &.is-visible {
    opacity: 1;
  }

  span {
    font-family: 'StyreneA';
    font-size: 12px;
    font-weight: 400;
    line-height: 15px;
    letter-spacing: 0em;
    text-align: center;
    cursor: pointer;
    color: white;

    @media (max-width: 568px) {
      display: none;
    }
  }
  @media (max-width: 568px) {
    width: 50px;
    height: 50px;
    border-radius: 50%;
  }
`;

const StyledScrollTopImage = styled.img`
  transform: rotate(90deg);
  height: 16px;
  width: 16px;
  cursor: pointer;
`;
